import { Injectable } from '@angular/core';
import { Observable, of, from } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import { GoogleApiDataService } from '../modules/google-places-auto-complete/google.api.data.service';

export interface BusinessProfile {
  placeId: string;
  name: string;
  logo?: string;
  address?: string;
  formattedAddress?: string;
  phone?: string;
  website?: string;
  domain?: string;
  rating?: number;
  userRatingsTotal?: number;
  types?: string[];
  addressComponents?: {
    streetNumber?: string;
    streetName?: string;
    addressLine1?: string;
    city?: string;
    state?: string;
    stateId?: any;
    country?: string;
    postalCode?: string;
  };
}

@Injectable({
  providedIn: 'root'
})
export class GoogleBusinessProfileService {

  private placesService: google.maps.places.PlacesService | null = null;
  private autocompleteService: google.maps.places.AutocompleteService | null = null;
  private isInitialized = false;

  constructor(private googleApiDataService: GoogleApiDataService) {
    this.initializeServices();
  }

  private async initializeServices(): Promise<void> {
    try {
      // Wait for Google Maps API to be loaded
      await this.googleApiDataService.api;

      // Create a hidden map element for PlacesService
      const mapDiv = document.createElement('div');
      mapDiv.style.display = 'none';
      document.body.appendChild(mapDiv);

      const map = new google.maps.Map(mapDiv, {
        center: { lat: 0, lng: 0 },
        zoom: 1
      });

      this.placesService = new google.maps.places.PlacesService(map);
      this.autocompleteService = new google.maps.places.AutocompleteService();
      this.isInitialized = true;
      console.log('Google Places services initialized');
    } catch (error) {
      console.error('Failed to initialize Google Places services:', error);
    }
  }

  // Search for businesses using Google Places Autocomplete
  searchBusinesses(query: string): Observable<BusinessProfile[]> {
    if (!query || query.trim() === '') {
      return of([]);
    }

    return new Observable(observer => {
      const checkAndSearch = () => {
        if (!this.isInitialized || !this.autocompleteService) {
          setTimeout(checkAndSearch, 100);
          return;
        }

        const request: google.maps.places.AutocompletionRequest = {
          input: query,
          types: ['establishment'],
          componentRestrictions: { country: 'us' }
        };

        this.autocompleteService!.getPlacePredictions(request, (predictions, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
            const businessProfiles: BusinessProfile[] = predictions.map(prediction => ({
              placeId: prediction.place_id,
              name: prediction.structured_formatting.main_text,
              address: prediction.structured_formatting.secondary_text,
              formattedAddress: prediction.description,
              types: prediction.types
            }));
            observer.next(businessProfiles);
          } else {
            observer.next([]);
          }
          observer.complete();
        });
      };

      checkAndSearch();
    });
  }

  // Get detailed business information
  getBusinessDetails(placeId: string): Observable<BusinessProfile> {
    return new Observable(observer => {
      const checkAndGetDetails = () => {
        if (!this.isInitialized || !this.placesService) {
          setTimeout(checkAndGetDetails, 100);
          return;
        }

        const request: google.maps.places.PlaceDetailsRequest = {
          placeId: placeId,
          fields: [
            'name',
            'formatted_address',
            'formatted_phone_number',
            'website',
            'rating',
            'user_ratings_total',
            'types',
            'address_components',
            'place_id'
          ]
        };

        this.placesService!.getDetails(request, (place, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK && place) {
            const businessProfile = this.mapPlaceToBusinessProfile(place);
            observer.next(businessProfile);
          } else {
            console.error(`Failed to get business details: ${status}`);
            observer.error(`Failed to get business details: ${status}`);
          }
          observer.complete();
        });
      };

      checkAndGetDetails();
    });
  }

  // Map Google Places result to BusinessProfile
  private mapPlaceToBusinessProfile(place: google.maps.places.PlaceResult): BusinessProfile {
    const businessProfile: BusinessProfile = {
      placeId: place.place_id || '',
      name: place.name || '',
      formattedAddress: place.formatted_address,
      phone: place.formatted_phone_number,
      website: place.website,
      rating: place.rating,
      userRatingsTotal: place.user_ratings_total,
      types: place.types
    };

    // Extract domain from website for logo lookup
    if (place.website) {
      businessProfile.domain = this.extractDomain(place.website);
    }

    // Parse address components
    if (place.address_components) {
      const addressComponents: any = {};
      place.address_components.forEach(component => {
        const types = component.types;
        if (types.includes('street_number')) {
          addressComponents.streetNumber = component.long_name;
        } else if (types.includes('route')) {
          addressComponents.streetName = component.long_name;
        } else if (types.includes('locality')) {
          addressComponents.city = component.long_name;
        } else if (types.includes('administrative_area_level_1')) {
          addressComponents.state = component.short_name;
          addressComponents.stateId = component.short_name; // For backend
        } else if (types.includes('country')) {
          addressComponents.country = component.long_name;
        } else if (types.includes('postal_code')) {
          addressComponents.postalCode = component.long_name;
        }
      });

      // Combine street number and street name for address_line_1
      if (addressComponents.streetNumber && addressComponents.streetName) {
        addressComponents.addressLine1 = `${addressComponents.streetNumber} ${addressComponents.streetName}`;
      } else if (addressComponents.streetName) {
        addressComponents.addressLine1 = addressComponents.streetName;
      }

      businessProfile.addressComponents = addressComponents;
    }

    return businessProfile;
  }

  // Extract domain from URL
  private extractDomain(url: string): string {
    try {
      const urlObj = new URL(url);
      return urlObj.hostname.replace('www.', '');
    } catch (error) {
      // If URL parsing fails, try simple string manipulation
      return url.replace(/^(?:https?:\/\/)?(?:www\.)?/i, '').split('/')[0];
    }
  }

  // Get company logo from Apistemic (Clearbit replacement)
  getClearbitLogo(domain: string): string {
    return `https://logos-api.apistemic.com/domain:${domain}`;
  }

  // Search businesses with full details (NO LOGO - logo will be fetched later in company-detail)
  searchBusinessesWithDetails(query: string): Observable<BusinessProfile[]> {
    return this.searchBusinesses(query).pipe(
      switchMap(businesses => {
        if (businesses.length === 0) {
          return of([]);
        }

        // Get detailed information for top 5 results (WITHOUT logo)
        const detailRequests = businesses.slice(0, 5).map(business =>
          this.getBusinessDetails(business.placeId).pipe(
            catchError(error => {
              console.error('Error getting details for', business.name, error);
              return of(business);
            })
          )
        );

        return from(Promise.all(detailRequests.map(req => req.toPromise())));
      }),
      map(results => results.filter(r => r !== null) as BusinessProfile[])
    );
  }

  // Format business for display
  formatBusinessForDisplay(business: BusinessProfile): any {
    return {
      placeId: business.placeId,
      name: business.name,
      logo: business.logo,
      address: business.formattedAddress,
      phone: business.phone,
      website: business.website,
      domain: business.domain,
      rating: business.rating,
      totalRatings: business.userRatingsTotal,
      types: business.types,
      addressComponents: business.addressComponents
    };
  }
}
